import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Download,
  CheckCircle2,
  Heart,
  AlertTriangle,
  FileQuestion,
  RefreshCw,
  Hourglass,
  Sparkles,
} from 'lucide-react';
import { CoupleSettings, AppDownloadLink } from '../types';
import { getExpirationDetails } from '../utils/appDownloadUtils';
import { usePwaInstall } from '../utils/usePwaInstall';
import { recordAppDownloadInDb, verifyAppDownloadTokenInDb } from '../services/firebaseDb';
import { romanticAudio } from '../utils/audio';

interface PublicAppDownloadPageProps {
  settings: CoupleSettings;
  isFirebaseConnected?: boolean;
  onUpdateSettings?: (newSettings: CoupleSettings) => void;
}

type LinkStatus = 'checking' | 'valid' | 'expired' | 'not_found';

// Helper para extraer el token desde cualquier formato de URL de forma síncrona
function extractTokenFromUrl(): string {
  if (typeof window === 'undefined') return '';
  try {
    const urlParams = new URLSearchParams(window.location.search);
    let detectedToken =
      urlParams.get('token') ||
      urlParams.get('t') ||
      urlParams.get('download') ||
      urlParams.get('d') ||
      '';

    if (!detectedToken && window.location.hash) {
      const hashClean = window.location.hash.replace(/^#\/?/, '');
      const hashParams = new URLSearchParams(hashClean);
      detectedToken =
        hashParams.get('token') ||
        hashParams.get('t') ||
        hashParams.get('download') ||
        '';
    }

    if (!detectedToken) {
      const parts = window.location.pathname.split('/').filter(Boolean);
      if (
        parts.length >= 2 &&
        (parts[0] === 'descargar-app' || parts[0] === 'descargar' || parts[0] === 'app')
      ) {
        detectedToken = parts[1];
      }
    }

    return (detectedToken || '').trim();
  } catch {
    return '';
  }
}

// Recupera enlaces cacheados localmente para resolución ultra-rápida (0ms)
function getCachedAppLinks(): AppDownloadLink[] {
  if (typeof window === 'undefined') return [];
  try {
    const rawLinks = localStorage.getItem('couple_app_links_cache');
    if (rawLinks) {
      const parsed = JSON.parse(rawLinks);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return [];
}

// Resuelve si un token coincide con los enlaces disponibles
function resolveMatchingLinkLocally(
  tokenToTest: string,
  candidateLinks: AppDownloadLink[]
): { found: AppDownloadLink | null; isExpired: boolean } {
  if (!Array.isArray(candidateLinks) || candidateLinks.length === 0) {
    return { found: null, isExpired: false };
  }

  const activeLinks = candidateLinks.filter((l) => l && l.isActive);

  // Si no hay token específico en la URL, buscar el enlace permanente o el activo general
  if (!tokenToTest || !tokenToTest.trim()) {
    const permanent = activeLinks.find((l) => l.duration === 'never');
    if (permanent) {
      return { found: permanent, isExpired: false };
    }
    const anyActive = activeLinks.find((l) => !getExpirationDetails(l).isExpired);
    if (anyActive) {
      return { found: anyActive, isExpired: false };
    }
    return { found: null, isExpired: false };
  }

  const clean = tokenToTest.trim().toLowerCase();
  const matched = candidateLinks.find(
    (l) =>
      l.token?.toLowerCase() === clean ||
      l.id?.toLowerCase() === clean ||
      (l.token && clean.length >= 6 && (l.token.toLowerCase().includes(clean) || clean.includes(l.token.toLowerCase())))
  );

  if (!matched) {
    return { found: null, isExpired: false };
  }

  const exp = getExpirationDetails(matched);
  return { found: matched, isExpired: exp.isExpired };
}

export const PublicAppDownloadPage: React.FC<PublicAppDownloadPageProps> = ({
  settings,
  onUpdateSettings,
}) => {
  const { isInstalled, platform, triggerInstall, isInAppBrowser, openInChrome } = usePwaInstall();

  // 1. Detección síncrona del token desde la URL
  const [token] = useState<string>(() => extractTokenFromUrl());

  // 2. Comprobación inicial síncrona:
  // Si el enlace ya está en memoria o en la caché local, habilitar de inmediato (0ms).
  // Si aún no está en caché, inicializar SIEMPRE en 'checking' (estado de carga elegante),
  // garantizando que JAMÁS se muestre un mensaje de 'El enlace no existe' antes de verificar con la base de datos.
  const initialResolution = useMemo(() => {
    const initialToken = extractTokenFromUrl();
    const current = settings.appDownloadLinks || [];
    const pool = current.length > 0 ? current : getCachedAppLinks();
    const localResult = resolveMatchingLinkLocally(initialToken, pool);

    if (localResult.found) {
      return {
        status: localResult.isExpired ? ('expired' as LinkStatus) : ('valid' as LinkStatus),
        link: localResult.found,
      };
    }

    return {
      status: 'checking' as LinkStatus,
      link: null,
    };
  }, []);

  const [linkStatus, setLinkStatus] = useState<LinkStatus>(initialResolution.status);
  const [matchedLink, setMatchedLink] = useState<AppDownloadLink | null>(initialResolution.link);
  const [localDownloadIncrement, setLocalDownloadIncrement] = useState<number>(0);
  const [installSuccess, setInstallSuccess] = useState<boolean>(false);
  const [isProcessingDownload, setIsProcessingDownload] = useState<boolean>(false);
  const [isRevalidating, setIsRevalidating] = useState<boolean>(false);

  // 3. Verificación asíncrona pero inmediata con Firestore
  useEffect(() => {
    let isSubscribed = true;

    const performAsyncDbCheck = async () => {
      // Si ya se resolvió localmente como válido y tenemos datos, igual sincronizamos en segundo plano
      const dbResult = await verifyAppDownloadTokenInDb(token);

      if (!isSubscribed) return;

      if (dbResult.status === 'valid') {
        setMatchedLink(dbResult.link);
        setLinkStatus('valid');
        if (dbResult.settings && onUpdateSettings) {
          onUpdateSettings(dbResult.settings);
        }
      } else if (dbResult.status === 'expired') {
        setMatchedLink(dbResult.link);
        setLinkStatus('expired');
      } else {
        // SOLO después de que la consulta a Firestore finalizó y confirmó que no existe
        // se muestra el estado de 'not_found'
        setMatchedLink(null);
        setLinkStatus('not_found');
      }
    };

    performAsyncDbCheck();

    return () => {
      isSubscribed = false;
    };
  }, [token]);

  // Manejador manual de reintento con estado de carga inmediato
  const handleRecheck = async () => {
    setIsRevalidating(true);
    setLinkStatus('checking');
    romanticAudio.playChime();

    try {
      const res = await verifyAppDownloadTokenInDb(token);
      if (res.status === 'valid') {
        setMatchedLink(res.link);
        setLinkStatus('valid');
        if (res.settings && onUpdateSettings) {
          onUpdateSettings(res.settings);
        }
      } else if (res.status === 'expired') {
        setMatchedLink(res.link);
        setLinkStatus('expired');
      } else {
        setMatchedLink(null);
        setLinkStatus('not_found');
      }
    } catch {
      setMatchedLink(null);
      setLinkStatus('not_found');
    } finally {
      setIsRevalidating(false);
    }
  };

  // Cálculo del total de descargas de la app
  const totalDownloads = useMemo(() => {
    const baseTotal = typeof settings.totalAppDownloads === 'number' ? settings.totalAppDownloads : 0;
    const linksTotal = (settings.appDownloadLinks || []).reduce(
      (acc, l) => acc + (l.downloadsCount || 0),
      0
    );
    const calculatedTotal = Math.max(baseTotal, linksTotal);
    return Math.max(0, calculatedTotal + localDownloadIncrement);
  }, [settings.totalAppDownloads, settings.appDownloadLinks, localDownloadIncrement]);

  // Manejador de Descarga e Instalación DIRECTA
  const handleDownloadApp = async () => {
    if (linkStatus !== 'valid') return;
    if (isProcessingDownload) return;

    setIsProcessingDownload(true);
    romanticAudio.playChime();

    // 1. Incrementar contador en pantalla y en la base de datos
    setLocalDownloadIncrement((prev) => prev + 1);
    try {
      await recordAppDownloadInDb(token || matchedLink?.id);
    } catch (err) {
      console.warn('Registro de descarga en nube:', err);
    }

    // 2. Si está en un navegador interno (WhatsApp, Instagram, etc.), abrir directamente en Google Chrome nativo
    if (platform === 'android' && isInAppBrowser) {
      openInChrome();
      setIsProcessingDownload(false);
      return;
    }

    // 3. Si está en un iframe (ej. preview de AI Studio), abrir directamente la ventana principal en Chrome
    const isInsideIframe = typeof window !== 'undefined' && window.self !== window.top;
    if (isInsideIframe) {
      const directUrl = window.location.origin + '/?source=pwa&autoInstall=1';
      window.open(directUrl, '_blank');
      setInstallSuccess(true);
      setIsProcessingDownload(false);
      return;
    }

    // 4. Lanzar instalación nativa directa PWA
    try {
      const outcome = await triggerInstall();
      if (outcome === 'accepted' || outcome === 'manual') {
        setInstallSuccess(true);
        romanticAudio.playBloomSuccess();
      }
    } catch {
      setInstallSuccess(true);
    } finally {
      setIsProcessingDownload(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1117] text-slate-100 flex flex-col justify-between font-['Plus_Jakarta_Sans',sans-serif] selection:bg-rose-500 selection:text-white relative overflow-x-hidden">
      {/* Luz ambiental de fondo */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[550px] h-[550px] bg-rose-600/15 rounded-full blur-[140px]" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[450px] h-[450px] bg-amber-500/10 rounded-full blur-[140px]" />
      </div>

      {/* Encabezado Superior */}
      <header className="relative z-10 w-full max-w-md mx-auto px-6 pt-8 pb-4 text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-rose-300 text-xs font-medium tracking-wide shadow-xs">
          <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400" />
          <span>App Oficial &bull; Nuestros Recuerdos</span>
        </div>
      </header>

      {/* Tarjeta Principal de Descarga */}
      <main className="relative z-10 w-full max-w-md mx-auto px-5 py-4 flex-1 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[#161b22]/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] text-center relative overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {/* ESTADO 1: VERIFICACIÓN ASÍNCRONA EN PROCESO (ESTADO DE CARGA ELEGANTE) */}
            {linkStatus === 'checking' && (
              <motion.div
                key="checking"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {/* Icono de la App con pulso sutil */}
                <div className="relative inline-block mx-auto">
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-tr from-[#21262d] to-[#161b22] border-2 border-rose-500/40 p-2 shadow-xl shadow-rose-950/40 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-3xl bg-rose-500/20 blur-xl animate-pulse" />
                    <img
                      src="/app-icon.svg"
                      alt="Nuestros Recuerdos"
                      className="relative z-10 w-full h-full object-contain rounded-2xl"
                    />
                  </div>
                </div>

                {/* Título e Indicador de Carga */}
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-semibold">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-rose-400" />
                    <span>Verificando enlace oficial...</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold font-serif text-white tracking-tight">
                    Nuestros Recuerdos
                  </h1>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                    Consultando la base de datos para habilitar la descarga segura en tu dispositivo...
                  </p>
                </div>

                {/* Estructura esqueleto para evitar saltos o parpadeos de interfaz */}
                <div className="space-y-3 pt-2">
                  <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-white/10" />
                    <div className="space-y-2 text-left w-36">
                      <div className="h-2.5 bg-white/10 rounded-full w-20" />
                      <div className="h-4 bg-white/10 rounded-full w-28" />
                    </div>
                  </div>

                  <div className="w-full py-4 px-6 rounded-2xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center gap-2 text-rose-300/60 font-semibold text-sm animate-pulse">
                    <Download className="w-4 h-4 opacity-50" />
                    <span>Preparando botón de descarga...</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ESTADO 2: ENLACE CONFIRMADO COMO NO EXISTENTE (SOLO TRAS CONSULTA COMPLETA A BD) */}
            {linkStatus === 'not_found' && (
              <motion.div
                key="not_found"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-6 py-2"
              >
                <div className="w-20 h-20 mx-auto rounded-3xl bg-rose-500/10 border-2 border-rose-500/30 text-rose-400 flex items-center justify-center shadow-lg shadow-rose-950/40">
                  <FileQuestion className="w-10 h-10" />
                </div>
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                    <span>Enlace No Encontrado</span>
                  </div>
                  <h1 className="text-2xl font-bold font-serif text-white tracking-tight">
                    El enlace no existe
                  </h1>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto">
                    El enlace de descarga que has abierto no se encuentra disponible o fue eliminado de la base de datos.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRecheck}
                  disabled={isRevalidating}
                  className="w-full py-3.5 px-4 rounded-2xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold border border-white/15 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 active:scale-[0.98]"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRevalidating ? 'animate-spin text-rose-400' : ''}`} />
                  <span>{isRevalidating ? 'Verificando...' : 'Reintentar comprobación'}</span>
                </button>
              </motion.div>
            )}

            {/* ESTADO 3: ENLACE EXPIRADO */}
            {linkStatus === 'expired' && (
              <motion.div
                key="expired"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-6 py-2"
              >
                <div className="w-20 h-20 mx-auto rounded-3xl bg-amber-500/10 border-2 border-amber-500/30 text-amber-400 flex items-center justify-center shadow-lg shadow-amber-950/40">
                  <Hourglass className="w-10 h-10" />
                </div>
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                    <Hourglass className="w-3.5 h-3.5 text-amber-400" />
                    <span>Enlace Vencido</span>
                  </div>
                  <h1 className="text-2xl font-bold font-serif text-white tracking-tight">
                    Este enlace ya venció
                  </h1>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto">
                    El periodo de vigencia asignado para este enlace de descarga ha expirado.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRecheck}
                  disabled={isRevalidating}
                  className="w-full py-3.5 px-4 rounded-2xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold border border-white/15 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 active:scale-[0.98]"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRevalidating ? 'animate-spin text-amber-400' : ''}`} />
                  <span>{isRevalidating ? 'Comprobando...' : 'Volver a comprobar'}</span>
                </button>
              </motion.div>
            )}

            {/* ESTADO 4: ENLACE VÁLIDO - INTERFAZ DIRECTA DE DESCARGA */}
            {linkStatus === 'valid' && (
              <motion.div
                key="valid"
                initial={{ opacity: 0, scale: 0.98, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-6"
              >
                {/* Icono de la Aplicación */}
                <div className="relative inline-block mx-auto">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-tr from-[#21262d] to-[#161b22] border-2 border-rose-500/30 p-2 shadow-xl shadow-rose-950/40 flex items-center justify-center">
                    <img
                      src="/app-icon.svg"
                      alt="Nuestros Recuerdos"
                      className="w-full h-full object-contain rounded-2xl"
                    />
                  </div>
                </div>

                {/* Título de la App */}
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold font-serif text-white tracking-tight">
                    Nuestros Recuerdos
                  </h1>
                  <p className="text-xs text-slate-400 mt-1">
                    Aplicación oficial para blog de pareja, cartas y jardín
                  </p>
                </div>

                {/* CONTADOR TOTAL DE DESCARGAS */}
                <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                    <Download className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <span className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                      Descargas en total:
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-black text-white tracking-tight">
                        {totalDownloads.toLocaleString()}
                      </span>
                      <span className="text-xs text-rose-300 font-semibold">
                        {totalDownloads === 1 ? 'descarga registrada' : 'descargas registradas'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Aviso de confirmación de instalación */}
                {installSuccess && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-3 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="font-semibold">¡Aplicación instalada con éxito en tu dispositivo!</span>
                  </motion.div>
                )}

                {/* BOTÓN DIRECTO DE DESCARGA DE LA APLICACIÓN */}
                <button
                  type="button"
                  id="btn-descargar-app-public"
                  onClick={handleDownloadApp}
                  disabled={isProcessingDownload}
                  className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-rose-500 via-rose-600 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-bold text-base shadow-lg shadow-rose-950/60 active:scale-[0.98] transition-all flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-75"
                >
                  <Download className={`w-5 h-5 ${isProcessingDownload ? 'animate-bounce' : ''}`} />
                  <span>{isProcessingDownload ? 'Descargando e Instalando...' : 'Descargar App'}</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Pie de página */}
      <footer className="relative z-10 w-full max-w-md mx-auto px-6 py-6 text-center text-xs text-slate-400">
        <p>
          Nuestros Recuerdos
          {settings?.partner1Name && settings?.partner2Name ? ` • ${settings.partner1Name} & ${settings.partner2Name}` : ''}
        </p>
      </footer>
    </div>
  );
};
